import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

export interface LookupTableItem {
  name: string;
  type?: string | number; // Optional type property (can be string or number)
  [key: string]: any; // Allow additional properties
}

export interface ColumnConfig {
  key: string;
  header: string;
  type: 'text' | 'number' | 'select' | 'boolean' | 'multiselect';
  sortable?: boolean;
  editable?: boolean; // Indicates if this column can be edited
  required?: boolean; // Indicates if this field is required when adding/editing
  selectOptions?: {value: any, label: string}[]; // Options for select/multiselect columns
}

// Special identifier for new items
const NEW_ITEM_ID = '__NEW_ITEM__';

@Component({
  selector: 'app-lookup-table',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatSortModule, MatPaginatorModule, MatIconModule, MatButtonModule, MatTooltipModule, MatInputModule, MatFormFieldModule, MatSelectModule, ProgressiveTypeaheadDirective, MatCheckboxModule, FormsModule, TextNormalizeDirective],
  templateUrl: './lookup-table.component.html',
  styleUrls: ['./lookup-table.component.scss']
})
export class LookupTableComponent<T extends LookupTableItem = LookupTableItem> implements OnChanges, AfterViewInit {
  dataSource = new MatTableDataSource<T>([]);
  displayedColumns: string[] = [];

  // Default column configuration for backward compatibility
  private defaultColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true }
  ];

  // Edit state management
  editingItemId: any = null;
  editingValues: {[columnKey: string]: any} = {}; // Track values for each column being edited

  /** Tooltip shown on the delete button of a protected (system-reserved) row. */
  readonly protectedDeleteTooltip = 'This entry is protected and cannot be deleted';

  // Add new item state
  isAddingNewItem: boolean = false;

  // Row selection state management
  matchedItems: T[] = [];
  rightItem: T | null = null;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement>;
  @ViewChild('addInput') addInput!: ElementRef<HTMLInputElement>;

  @Input() items: T[] | null = [];
  @Input() emptyMessage: string = 'No items to display';
  @Input() enablePaging: boolean = false;
  @Input() pageSize: number = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 100];
  @Input() enableMatching: boolean = false;
  @Input() columns: ColumnConfig[] = []; // Custom column configuration
  @Input() serverSidePagination: boolean = false; // Enable server-side pagination
  @Input() totalCount: number = 0; // Total count for server-side pagination
  @Input() initialSortColumn: string = ''; // Initial sort column
  @Input() initialSortDirection: 'asc' | 'desc' | '' = ''; // Initial sort direction
  @Input() readOnlyField: string = ''; // Field name that marks items as read-only (hides edit/delete, bold text)

  @Output() itemClick = new EventEmitter<T>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() editClick = new EventEmitter<T>();
  @Output() deleteClick = new EventEmitter<T>();
  @Output() itemSave = new EventEmitter<{item: T, newValue: string, columnKey: string} | {item: T, newName: string}>();
  @Output() itemAdd = new EventEmitter<{[key: string]: any}>();
  @Output() readyToMatch = new EventEmitter<{rightItem?: T, matchedItems?: T[]}>();

  constructor() {
    this.initializeColumns();
    this.updateDataSource();
    this.setupCustomSorting();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // This lifecycle hook is crucial. It fires when any @Input() property changes.
    if (changes['columns']) {
      this.initializeColumns();
    }
    if (changes['items']) {
      this.updateDataSource();
      // Force paginator reconnection when items change significantly
      this.reconnectPaginator();
    }
  }

  private initializeColumns(): void {
    // Use custom columns if provided, otherwise use default columns
    const columnsToUse = this.columns.length > 0 ? this.columns : this.defaultColumns;
    this.displayedColumns = [...columnsToUse.map(col => col.key), 'actions'];
  }

  private setupCustomSorting(): void {
    this.dataSource.sortingDataAccessor = (item: T, property: string) => {
      // Handle special cases for select columns and object properties
      if (this.isSelectColumn(property)) {
        return this.getSelectColumnDisplayValue(item, property);
      }

      // Use the existing getColumnValue method which handles object properties
      return this.getColumnValue(item, property);
    };
  }

  getColumns(): ColumnConfig[] {
    return this.columns.length > 0 ? this.columns : this.defaultColumns;
  }

  getColumnValue(item: T, columnKey: string): any {
    const value = (item as any)[columnKey];

    // If the value is an object and has a 'name' property, return the name
    if (value && typeof value === 'object' && value.name !== undefined) {
      return value.name;
    }

    return value;
  }

  // Helper method to get the appropriate value for select columns
  getSelectColumnValue(item: T, columnKey: string): any {
    const value = (item as any)[columnKey];
    const column = this.getColumns().find(col => col.key === columnKey);

    if (column?.type === 'select' && value) {
      // For select columns, check if the value is an object and extract the appropriate property
      if (typeof value === 'object') {
        // If it's an object, try to find a matching option and return its value
        const options = this.getSelectOptions(columnKey);
        const matchingOption = options.find(option =>
          option.label === value.name ||
          option.value === value.id ||
          option.value === value.name
        );
        return matchingOption ? matchingOption.value : value.name || value.id;
      }
    }

    return this.getColumnValue(item, columnKey);
  }

  isEditableColumn(columnKey: string): boolean {
    const column = this.getColumns().find(col => col.key === columnKey);
    return (column?.type === 'text' || column?.type === 'number') && column?.editable === true;
  }

  isNumberColumn(columnKey: string): boolean {
    const column = this.getColumns().find(col => col.key === columnKey);
    return column?.type === 'number';
  }

  canEditItems(): boolean {
    // Items can be edited if there's any editable column (text, select, boolean, or multiselect)
    return this.getColumns().some(col =>
      (col.type === 'text' && col.editable) ||
      (col.type === 'select' && col.editable) ||
      (col.type === 'boolean' && col.editable) ||
      (col.type === 'multiselect' && col.editable)
    );
  }

  hasValidEditingValues(): boolean {
    const columns = this.getColumns();
    const requiredColumns = columns.filter(col => col.required && col.editable);

    // If there are required columns, all of them must have valid values
    if (requiredColumns.length > 0) {
      const allRequiredValid = requiredColumns.every(column => {
        const value = this.editingValues[column.key];

        if (column.type === 'boolean') {
          // Boolean values are always valid (true or false)
          return true;
        }

        if (column.type === 'select') {
          // For select columns, value must be selected (not null/undefined/empty)
          return value !== null && value !== undefined && value !== '';
        }

        if (column.type === 'multiselect') {
          // For multiselect, array must have at least one item
          return Array.isArray(value) && value.length > 0;
        }

        // For text columns, check if value is not empty
        return value !== null && value !== undefined && value.toString().trim() !== '';
      });

      return allRequiredValid;
    }

    // If there are no required columns, check if any editable column has a valid value (existing behavior)
    return Object.entries(this.editingValues).some(([columnKey, value]) => {
      const column = columns.find(col => col.key === columnKey);
      if (column?.type === 'boolean') {
        // Boolean values are always valid (true or false)
        return true;
      }
      // For text and select columns, check if value is not empty
      return value !== null && value !== undefined && value.toString().trim() !== '';
    });
  }

  getEditingValue(columnKey: string): any {
    return this.editingValues[columnKey];
  }

  isSelectColumn(columnKey: string): boolean {
    const column = this.getColumns().find(col => col.key === columnKey);
    return column?.type === 'select';
  }

  isBooleanColumn(columnKey: string): boolean {
    const column = this.getColumns().find(col => col.key === columnKey);
    return column?.type === 'boolean';
  }

  isMultiSelectColumn(columnKey: string): boolean {
    const column = this.getColumns().find(col => col.key === columnKey);
    return column?.type === 'multiselect';
  }

  getSelectOptions(columnKey: string): {value: string | number, label: string}[] {
    const column = this.getColumns().find(col => col.key === columnKey);
    return column?.selectOptions || [];
  }

  getSelectColumnDisplayValue(item: T, columnKey: string): string {
    const value = (item as any)[columnKey];
    const column = this.getColumns().find(col => col.key === columnKey);

    if (column?.type === 'select' && value !== null && value !== undefined) {
      const options = this.getSelectOptions(columnKey);

      // Handle enums by finding the option where the value matches
      if (typeof value === 'number') {
        const matchingOption = options.find(option => (option.value as any) === value);
        if (matchingOption) {
          return matchingOption.label;
        }
      }

      // Handle objects by finding matching option
      if (typeof value === 'object') {
        // Try to find a matching option by comparing the entire object, id, or name
        const matchingOption = options.find(option => {
          if (typeof option.value === 'object' && option.value !== null) {
            // Compare by id if both have id property
            if ('id' in value && 'id' in option.value) {
              return value.id === (option.value as any).id;
            }
            // Compare by name if both have name property
            if ('name' in value && 'name' in option.value) {
              return value.name === (option.value as any).name;
            }
          }
          return false;
        });

        if (matchingOption) {
          return matchingOption.label;
        }

        // Fallback to object's name property if it exists
        if ('name' in value && value.name) {
          return value.name;
        }
      }

      // Fallback for simple string values
      const matchingOption = options.find(option => option.value === value);
      return matchingOption ? matchingOption.label : value.toString();
    }

    return value;
  }

  getMultiSelectColumnDisplayValue(item: T, columnKey: string): string {
    const values = (item as any)[columnKey];

    if (!values || !Array.isArray(values) || values.length === 0) {
      return '';
    }

    // For arrays of objects (like Country[]), extract names and sort alphabetically
    if (values.length > 0 && typeof values[0] === 'object' && 'name' in values[0]) {
      const names = values.map(v => v.name).sort((a, b) => a.localeCompare(b));
      if (names.length > 2) {
        return `${names[0]}, ${names[1]} +${names.length - 2} more`;
      }
      return names.join(', ');
    }

    // For arrays of primitives, sort alphabetically
    const sortedValues = [...values].sort((a, b) => String(a).localeCompare(String(b)));
    if (sortedValues.length > 2) {
      return `${sortedValues[0]}, ${sortedValues[1]} +${sortedValues.length - 2} more`;
    }
    return sortedValues.join(', ');
  }

  getMultiSelectColumnTooltip(item: T, columnKey: string): string {
    const values = (item as any)[columnKey];

    if (!values || !Array.isArray(values) || values.length === 0) {
      return '';
    }

    // For arrays of objects (like Country[]), extract names and sort alphabetically
    if (values.length > 0 && typeof values[0] === 'object' && 'name' in values[0]) {
      return values.map(v => v.name).sort((a, b) => a.localeCompare(b)).join(', ');
    }

    // For arrays of primitives, sort alphabetically
    return [...values].sort((a, b) => String(a).localeCompare(String(b))).join(', ');
  }

  ngAfterViewInit() {
    // Set initial sort state if provided
    if (this.initialSortColumn && this.initialSortDirection && this.sort) {
      this.sort.active = this.initialSortColumn;
      this.sort.direction = this.initialSortDirection;
    }

    // Only reconnect sort for client-side pagination
    // For server-side pagination, we only need the matSortChange event, not the dataSource connection
    if (!this.serverSidePagination) {
      this.reconnectSort();
    }
    if (this.enablePaging) {
      if (this.serverSidePagination) {
        // For server-side pagination, don't connect paginator to dataSource
        // The parent component will handle data loading
        this.dataSource.paginator = null;
      } else {
        // For client-side pagination, connect paginator to dataSource
        this.dataSource.paginator = this.paginator;
      }
    }
    // Ensure custom sorting is set up after sort is connected (only for client-side)
    if (!this.serverSidePagination) {
      this.setupCustomSorting();
    }
    // Ensure paginator is properly connected after view initialization
    this.reconnectPaginator();
  }

      private updateDataSource(): void {
    const data = this.items ?? [];
    let newData: T[];

    if (this.isAddingNewItem) {
      // Add a temporary new item at the beginning
      const newItem = { name: '', [NEW_ITEM_ID]: true } as unknown as T;
      newData = [newItem, ...data];
    } else {
      // Create a new array reference to ensure change detection
      newData = [...data];
    }

    this.dataSource.data = newData;
    // Ensure custom sorting is maintained after data update (only for client-side)
    if (!this.serverSidePagination) {
      this.setupCustomSorting();
      // Reconnect sort if available (important for async data)
      this.reconnectSort();
    }
  }

    private reconnectPaginator(): void {
    if (!this.enablePaging) {
      return;
    }

    // Use setTimeout to ensure the paginator is rendered in the DOM
    setTimeout(() => {
      if (this.paginator) {
        if (this.serverSidePagination) {
          // For server-side pagination, set the total length but don't connect to dataSource
          this.paginator.length = this.totalCount;
          // Don't reconnect paginator to dataSource for server-side pagination
        } else {
          // For client-side pagination, follow the existing logic
          // Store current page index before reconnecting
          const currentPageIndex = this.paginator.pageIndex;

          // Force reconnection by temporarily disconnecting and reconnecting
          this.dataSource.paginator = null;
          this.dataSource.paginator = this.paginator;

          // If we're adding a new item, always go to first page so user can see the new item
          if (this.isAddingNewItem) {
            this.paginator.firstPage();
          } else if (this.dataSource.data.length > 0) {
            // Otherwise, restore the previous page index if it's still valid
            const maxPageIndex = Math.ceil(this.dataSource.data.length / this.paginator.pageSize) - 1;
            if (currentPageIndex <= maxPageIndex) {
              // Restore the previous page
              this.paginator.pageIndex = currentPageIndex;
            } else {
              // If the current page is now out of bounds, go to the last valid page
              this.paginator.pageIndex = Math.max(0, maxPageIndex);
            }
          }
        }
      } else if (this.dataSource.data.length > 0 || (this.serverSidePagination && this.totalCount > 0)) {
        // If paginator is not yet available but we have data, try again after a longer delay
        setTimeout(() => this.reconnectPaginator(), 100);
      }
    }, 0);
  }

  private reconnectSort(): void {
    // Use setTimeout to ensure the sort is rendered in the DOM
    setTimeout(() => {
      if (this.sort) {
        // Reconnect the sort to handle async data loading
        this.dataSource.sort = this.sort;
      } else if (this.dataSource.data.length > 0) {
        // If sort is not yet available but we have data, try again after a delay
        setTimeout(() => this.reconnectSort(), 100);
      }
    }, 0);
  }

  onItemClick(item: T): void {
    // Don't emit item click if we're in edit mode or add mode or it's the new item
    if (this.editingItemId === null && !this.isAddingNewItem && !this.isNewItem(item)) {
      this.itemClick.emit(item);
    }
  }

  // Get the current state of an item
  getItemState(item: T): 'normal' | 'matched' | 'right' {
    if (!this.enableMatching) {
      return 'normal';
    }

    if (this.rightItem === item) {
      return 'right';
    }
    if (this.matchedItems.includes(item)) {
      return 'matched';
    }
    return 'normal';
  }

  // Handle Match button click
  onMatchClick(item: T, event: Event): void {
    if (!this.enableMatching) {
      return;
    }

    event.stopPropagation(); // Prevent row click

    // If item is already matched, remove it from matchedItems
    if (this.matchedItems.includes(item)) {
      this.matchedItems = this.matchedItems.filter(matchedItem => matchedItem !== item);
    } else {
      // If item is the rightItem, remove it from rightItem first
      if (this.rightItem === item) {
        this.rightItem = null;
      }
      // Add to matchedItems
      this.matchedItems.push(item);
    }

    this.checkReadyToMatch();
  }

  // Handle Right button click
  onRightClick(item: T, event: Event): void {
    if (!this.enableMatching) {
      return;
    }

    event.stopPropagation(); // Prevent row click

    // If item is already the rightItem, remove it
    if (this.rightItem === item) {
      this.rightItem = null;
    } else {
      // Remove from matchedItems if it's there
      this.matchedItems = this.matchedItems.filter(matchedItem => matchedItem !== item);
      // Set as rightItem
      this.rightItem = item;
    }

    this.checkReadyToMatch();
  }

  // Check if we should emit readyToMatch event
  private checkReadyToMatch(): void {
    if (!this.enableMatching) {
      return;
    }

    if (this.rightItem && this.matchedItems.length > 0) {
      this.readyToMatch.emit({
        rightItem: this.rightItem,
        matchedItems: [...this.matchedItems]
      });
    } else {
      this.readyToMatch.emit({
        rightItem: undefined,
        matchedItems: undefined
      });
    }
  }

  // Public method to clear matching state - call after successful merge operations
  public clearMatchingState(): void {
    this.matchedItems = [];
    this.rightItem = null;
    this.readyToMatch.emit({
      rightItem: undefined,
      matchedItems: undefined
    });
  }

  onMatSortChange(sort: Sort): void {
    this.sortChange.emit(sort);
  }

  onPageChangeEvent(event: PageEvent): void {
    this.pageChange.emit(event);
  }

  onEditClick(item: T, event: Event): void {
    this.cancelEdit();
    this.cancelAdd();
    event.stopPropagation(); // Prevent row click
    this.startEdit(item);
  }

  onDeleteClick(item: T, event: Event): void {
    event.stopPropagation(); // Prevent row click
    if (this.isDeleteProtected(item)) return; // system-reserved rows cannot be deleted
    this.cancelEdit();
    this.cancelAdd();
    this.deleteClick.emit(item);
  }

  // A system-reserved row is referenced by program logic and must not be deleted.
  isDeleteProtected(item: T): boolean {
    return Boolean((item as any)?.systemReserved);
  }

      startEdit(item: T): void {
    // Initialize editing values for all editable columns
    this.editingItemId = this.getItemId(item);
    this.editingValues = {};

    // Set initial values for all editable columns
    this.getColumns().forEach(col => {
      if ((col.type === 'text' && col.editable) ||
          (col.type === 'number' && col.editable) ||
          (col.type === 'select' && col.editable) ||
          (col.type === 'boolean' && col.editable) ||
          (col.type === 'multiselect' && col.editable)) {
        if (col.type === 'select' || col.type === 'multiselect') {
          // For select/multiselect columns, we need the actual value (which could be an object or array)
          this.editingValues[col.key] = (item as any)[col.key];
        } else {
          this.editingValues[col.key] = this.getColumnValue(item, col.key);
        }
      }
    });

    // Ensure change detection runs and then focus the first text input
    setTimeout(() => {
      // Force change detection for mat-select components
      this.editingValues = { ...this.editingValues };

      if (this.editInput) {
        this.editInput.nativeElement.focus();
        this.editInput.nativeElement.select();
      }
    }, 0);
  }

          saveEdit(item: T): void {
    // Save all changed columns
    const editableColumns = this.getColumns().filter(col =>
      (col.type === 'text' && col.editable) ||
      (col.type === 'number' && col.editable) ||
      (col.type === 'select' && col.editable) ||
      (col.type === 'boolean' && col.editable) ||
      (col.type === 'multiselect' && col.editable)
    );

    editableColumns.forEach(column => {
      const newValue = this.editingValues[column.key];
      const currentValue = (item as any)[column.key];

      // Check if value has actually changed
      let hasChanged = false;

      if (column.type === 'multiselect' && Array.isArray(newValue) && Array.isArray(currentValue)) {
        // For multiselect columns with arrays of objects, compare by ids
        if (newValue.length !== currentValue.length) {
          hasChanged = true;
        } else {
          const newIds = newValue.map(v => (typeof v === 'object' && 'id' in v) ? v.id : v).sort();
          const currentIds = currentValue.map(v => (typeof v === 'object' && 'id' in v) ? v.id : v).sort();
          hasChanged = JSON.stringify(newIds) !== JSON.stringify(currentIds);
        }
      } else if (column.type === 'select' && typeof newValue === 'object' && typeof currentValue === 'object') {
        // For select columns with objects, compare by id or the whole object
        if (newValue && currentValue && 'id' in newValue && 'id' in currentValue) {
          hasChanged = newValue.id !== currentValue.id;
        } else {
          hasChanged = newValue !== currentValue;
        }
      } else {
        // For text and boolean columns, simple comparison
        hasChanged = newValue !== undefined && newValue !== currentValue;
      }

      if (hasChanged) {
        // For text columns, trim the value; for number columns, convert; for select, boolean, and multiselect columns, use as-is
        const finalValue = column.type === 'text' ? newValue.trim() : column.type === 'number' ? Number(newValue) : newValue;

        if ((column.type === 'text' && finalValue !== '' && finalValue !== null) ||
            (column.type === 'number') ||
            (column.type === 'select' && finalValue !== null) ||
            (column.type === 'boolean') ||
            (column.type === 'multiselect')) {
          // For backward compatibility, emit different event structures
          if (column.key === 'name' && editableColumns.length === 1) {
            // Simple lookup table with only name column - use legacy format
            this.itemSave.emit({
              item,
              newName: finalValue
            } as any);
          } else {
            // Multi-column table - use new format
            this.itemSave.emit({
              item,
              newValue: finalValue,
              columnKey: column.key
            });
          }
        }
      }
    });

    this.cancelEdit();
  }

  cancelEdit(): void {
    this.editingItemId = null;
    this.editingValues = {};
  }

  isEditing(item: T): boolean {
    return this.editingItemId === this.getItemId(item);
  }

  private getItemId(item: T): any {
    // Use a unique identifier if available, otherwise use the first editable column value, otherwise use the item reference
    const editableColumn = this.getColumns().find(col => (col.type === 'text' || col.type === 'number') && col.editable);
    return (item as any).id || (editableColumn ? this.getColumnValue(item, editableColumn.key) : null) || item;
  }

  onKeyDown(event: KeyboardEvent, item: T): void {
    if (event.key === 'Enter') {
      this.saveEdit(item);
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  // Add new item methods - now public to be called from parent
  public startAdd(): void {
    this.cancelEdit();
    this.isAddingNewItem = true;

    // Initialize editing values for all editable columns
    this.editingValues = {};
    this.getColumns().forEach(col => {
      if (col.editable) {
        if (col.type === 'boolean') {
          this.editingValues[col.key] = false;
        } else if (col.type === 'multiselect') {
          this.editingValues[col.key] = [];
        } else {
          this.editingValues[col.key] = '';
        }
      }
    });

    this.updateDataSource();

    // Go to first page so user can see the new item
    this.reconnectPaginator();

    // Focus the input after the view updates
    setTimeout(() => {
      if (this.addInput) {
        this.addInput.nativeElement.focus();
      }
    }, 0);
  }

        saveAdd(): void {
    const editableColumns = this.getColumns().filter(col => col.editable);
    const newItem: {[key: string]: any} = {};

    // Build the new item from all editable columns with valid values
    let hasValidData = false;
    editableColumns.forEach(column => {
      const value = this.editingValues[column.key];
      if (column.type === 'boolean') {
        newItem[column.key] = value || false;
        hasValidData = true; // Boolean fields are always valid
      } else if (column.type === 'number') {
        const numValue = value != null && value !== '' ? Number(value) : 0;
        newItem[column.key] = numValue;
        hasValidData = true;
      } else if (value && value.toString().trim() !== '') {
        newItem[column.key] = column.type === 'text' ? value.trim() : value;
        hasValidData = true;
      }
    });

    if (hasValidData) {
      this.itemAdd.emit(newItem);
    }
    this.cancelAdd();
  }

  cancelAdd(): void {
    this.isAddingNewItem = false;
    this.editingValues = {};
    this.updateDataSource();
  }

  onAddKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.saveAdd();
    } else if (event.key === 'Escape') {
      this.cancelAdd();
    }
  }

  compareWith(o1: any, o2: any): boolean {
    if (o1 && o2 && typeof o1 === 'object' && typeof o2 === 'object' && o1.id && o2.id) {
      return o1.id === o2.id;
    }
    return o1 === o2;
  }

  isReadOnly(item: T): boolean {
    return this.readOnlyField !== '' && !!(item as any)[this.readOnlyField];
  }

  isNewItem(item: T): boolean {
    return (item as any)[NEW_ITEM_ID] === true;
  }

  isAddingThisItem(item: T): boolean {
    return this.isNewItem(item) && this.isAddingNewItem;
  }

  // Get CSS classes for a row based on its state
  getRowClasses(item: T): string {
    const classes: string[] = [];

    if (this.isNewItem(item)) {
      classes.push('add-row');
    }

    if (this.isReadOnly(item)) {
      classes.push('read-only-row');
    }

    if (this.enableMatching) {
      const state = this.getItemState(item);
      if (state === 'matched') {
        classes.push('matched-row');
      } else if (state === 'right') {
        classes.push('right-row');
      }
    }

    const result = classes.join(' ');
    return result;
  }
}
